/* Feb-2026 · Admin — ISBN Bank editor.
 *
 * Table of every ISBN in the pool with:
 *   • filter chips (available / assigned / retired / all)
 *   • assign row-inline (project selector + format dropdown)
 *   • unassign flip
 *   • edit note in-row
 *   • Import block (paste ISBNs → bulk-import parses + dedupes)
 *   • Structure column — Prefix / Group / Registrant / Publication / Check
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { BookOpenCheck, Loader2, RefreshCw, X, PlusCircle } from 'lucide-react';
import { adminApi } from '@/lib/api';

const FORMATS = ['ebook', 'paperback', 'hardcover', 'audiobook'];

const TONE = {
  available: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
  assigned:  'bg-primary/10 text-primary border-primary/40',
  retired:   'bg-muted/50 text-muted-foreground border-border',
};

export default function IsbnBankPanel() {
  const [rows,    setRows]    = useState([]);
  const [tallies, setTallies] = useState({});
  const [filter,  setFilter]  = useState('available');
  const [loading, setLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [rawImport,  setRawImport]  = useState('');
  const [assignFor, setAssignFor]  = useState(null);  // row currently in assign-mode
  const [projectId, setProjectId]  = useState('');
  const [format,    setFormat]     = useState('ebook');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter && filter !== 'all' ? { status: filter } : {};
      const r = await adminApi.isbnAssignmentBankList(params);
      setRows(r.isbns || []);
      setTallies(r.tallies || {});
    } catch { toast.error('Failed to load ISBN bank.'); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const submitImport = async () => {
    if (!rawImport.trim()) return toast.error('Paste some ISBNs first.');
    try {
      const r = await adminApi.isbnAssignmentBankBulkImport({ raw: rawImport });
      toast.success(`Imported ${r.inserted.length} new · ${r.already.length} already-known · ${r.invalid.length} invalid`);
      setImportOpen(false); setRawImport(''); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Import failed.'); }
  };

  const doAssign = async (isbn) => {
    if (!projectId.trim()) return toast.error('Enter a project id first.');
    try {
      await adminApi.isbnAssignmentBankAssign(isbn, { project_id: projectId.trim(), format });
      toast.success(`Assigned ${isbn} → ${projectId} (${format}).`);
      setAssignFor(null); setProjectId(''); load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((typeof d === 'string' ? d : d?.message) || 'Assignment failed.');
    }
  };

  const doUnassign = async (isbn) => {
    if (!window.confirm(`Release ${isbn} back to available?`)) return;
    try {
      await adminApi.isbnAssignmentBankUnassign(isbn, {});
      toast.success(`Released ${isbn}.`);
      load();
    } catch { toast.error('Unassign failed.'); }
  };

  return (
    <div className="p-4" data-testid="isbn-bank-panel">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 text-[14px] font-semibold">
          <BookOpenCheck className="h-4 w-4 text-primary" /> ISBN Bank
        </div>
        <div className="ml-auto flex items-center gap-1">
          {['available', 'assigned', 'retired', 'all'].map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => setFilter(id)}
              data-testid={`isbn-filter-${id}`}
              className={`inline-flex items-center gap-1 rounded-sm border px-2 py-1 text-[11px] font-semibold ${
                filter === id
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-background text-muted-foreground hover:bg-secondary'
              }`}
            >
              {id}
              {id !== 'all' && tallies[id] != null && (
                <span className="rounded-full bg-secondary px-1.5 text-[10px]">{tallies[id]}</span>
              )}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setImportOpen((v) => !v)}
            data-testid="isbn-import-toggle"
            className="ml-2 inline-flex items-center gap-1 rounded-sm border border-primary/40 bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/20"
          >
            <PlusCircle className="h-3 w-3" /> Import
          </button>
          <button
            type="button"
            onClick={load}
            data-testid="isbn-refresh"
            className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[11px] text-muted-foreground hover:bg-secondary"
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          </button>
        </div>
      </div>

      {importOpen && (
        <div className="mb-3 rounded-md border border-primary/30 bg-primary/5 p-3" data-testid="isbn-import-form">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-primary">
            Paste new ISBNs (whitespace / comma / tab separated). Duplicates & bad check-digits are skipped.
          </div>
          <textarea
            value={rawImport}
            onChange={(e) => setRawImport(e.target.value)}
            rows={5}
            data-testid="isbn-import-textarea"
            className="w-full rounded-sm border border-border bg-background p-2 font-mono text-[11.5px]"
            placeholder="979-8-953072-00-7 979-8-953072-01-4 ..."
          />
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button" onClick={submitImport}
              data-testid="isbn-import-submit"
              className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:opacity-90"
            >
              Import & Validate
            </button>
            <button
              type="button" onClick={() => setImportOpen(false)}
              data-testid="isbn-import-cancel"
              className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[11px] text-muted-foreground hover:bg-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-[11.5px]">
          <thead className="bg-panel-header/60 text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="p-2 text-left">ISBN</th>
              <th className="p-2 text-left">Structure</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-left">Assigned to</th>
              <th className="p-2 text-left">Format</th>
              <th className="p-2 text-left">Note</th>
              <th className="p-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.isbn} data-testid={`isbn-row-${r.isbn}`} className="border-t border-border">
                <td className="p-2 font-mono">{r.isbn_formatted}</td>
                <td className="p-2 font-mono text-muted-foreground">
                  {r.prefix_element}·{r.registration_group}·{r.registrant_element}·{r.publication_element}·{r.check_digit}
                </td>
                <td className="p-2">
                  <span className={`inline-flex rounded-full border px-2 py-[1px] text-[10px] font-semibold ${TONE[r.status] || 'border-border'}`}>
                    {r.status}
                  </span>
                </td>
                <td className="p-2 font-mono text-[10.5px]">{r.assigned_project_id || '—'}</td>
                <td className="p-2">{r.assigned_format || '—'}</td>
                <td className="p-2 max-w-[220px] truncate text-muted-foreground">{r.note || '—'}</td>
                <td className="p-2 text-right">
                  {r.status === 'available' && assignFor === r.isbn ? (
                    <div className="flex items-center justify-end gap-1">
                      <input
                        value={projectId}
                        onChange={(e) => setProjectId(e.target.value)}
                        placeholder="proj_xxx"
                        data-testid={`isbn-assign-project-${r.isbn}`}
                        className="h-7 w-32 rounded-sm border border-border bg-background px-2 text-[11px]"
                      />
                      <select
                        value={format}
                        onChange={(e) => setFormat(e.target.value)}
                        data-testid={`isbn-assign-format-${r.isbn}`}
                        className="h-7 rounded-sm border border-border bg-background px-2 text-[11px]"
                      >
                        {FORMATS.map((f) => (<option key={f} value={f}>{f}</option>))}
                      </select>
                      <button
                        type="button" onClick={() => doAssign(r.isbn)}
                        data-testid={`isbn-assign-confirm-${r.isbn}`}
                        className="inline-flex items-center rounded-sm bg-primary px-2 py-1 text-[10.5px] font-semibold text-primary-foreground"
                      >Save</button>
                      <button
                        type="button" onClick={() => setAssignFor(null)}
                        data-testid={`isbn-assign-cancel-${r.isbn}`}
                        className="rounded-sm p-1 hover:bg-secondary"
                      ><X className="h-3 w-3" /></button>
                    </div>
                  ) : r.status === 'available' ? (
                    <button
                      type="button" onClick={() => { setAssignFor(r.isbn); setProjectId(''); setFormat('ebook'); }}
                      data-testid={`isbn-assign-btn-${r.isbn}`}
                      className="inline-flex items-center rounded-sm border border-primary/40 bg-primary/10 px-2 py-[3px] text-[10.5px] font-semibold text-primary hover:bg-primary/20"
                    >Assign</button>
                  ) : r.status === 'assigned' ? (
                    <button
                      type="button" onClick={() => doUnassign(r.isbn)}
                      data-testid={`isbn-unassign-btn-${r.isbn}`}
                      className="inline-flex items-center rounded-sm border border-red-500/40 bg-red-500/10 px-2 py-[3px] text-[10.5px] font-semibold text-red-700 hover:bg-red-500/20 dark:text-red-300"
                    >Release</button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length === 0 && !loading && (
        <div className="mt-6 rounded-md border border-dashed border-border bg-panel-header/40 p-6 text-center text-[12.5px] text-muted-foreground">
          No ISBNs in this bucket.
        </div>
      )}
    </div>
  );
}
